import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLayoutContext } from "../context/LayoutContext";
import api from "../hooks/api";
import { API_URLS } from "../App";
import { OmsOrder, OmsOrdersListResponse, OrderStatus } from "../model/Oms";
import OmsOrderDetail from "../components/OmsOrders/OmsOrderDetail";
import ListOmsOrderItems from "../components/OmsOrders/ListOmsOrderItems";
import { orderShortRef } from "../helpers/orderRef";
import TenantSelector from "../components/common/TenantSelector";
import { useTenantSelection } from "../hooks/useTenantSelection";

const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING_OFFER: "Pendente Proposta",
  PAYMENT_PENDING: "Pag. Pendente",
  PAID: "Pago",
  FULFILLMENT_INIT: "A Processar",
  PAID_PENDING_FULFILLMENT: "Pago — Ful. Pendente",
  COMPLETED: "Concluído",
  REFUNDED: "Reembolsado",
  FAILED: "Falhado",
  CANCELLED: "Cancelado",
  OFFER_SENT: "Proposta Enviada",
};

const FIELD_CLASS =
  "block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200";

const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-2";

const PRIMARY_BUTTON_CLASS =
  "px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors";

const SECONDARY_BUTTON_CLASS =
  "px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors";

// ── Helpers ────────────────────────────────────────────────────────────────

function getInitialParam(key: string, fallback = ""): string {
  return new URLSearchParams(window.location.search).get(key) ?? fallback;
}

function getInitialPage(): number {
  const p = parseInt(getInitialParam("page", "1"), 10);
  return isNaN(p) || p < 1 ? 1 : p;
}

// ── Page ───────────────────────────────────────────────────────────────────

const OmsOrdersPage: React.FC = () => {
  const { profile, isAuthenticated } = useAuth();
  const { setModalScreen, setError } = useLayoutContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams<{ orderId?: string }>();
  const authed = isAuthenticated();

  const [userProfile, setUserProfile] = useState<ProfileAndSettings | null>(
    null,
  );
  const [profileLoading, setProfileLoading] = useState(true);
  const [needsRelogin, setNeedsRelogin] = useState(false);

  // List state
  const [orders, setOrders] = useState<OmsOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(getInitialPage);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [perPage, setPerPage] = useState(20);

  // Sort state
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filter state — initialised from URL
  const [search, setSearch] = useState(() => getInitialParam("search"));
  const [statusFilter, setStatusFilter] = useState<OrderStatus | "">(() => {
    const raw = getInitialParam("status");
    // Guard against hand-edited ?status=FOO (select with no matching option
    // would render blank + send the bogus value to the API).
    return raw in ORDER_STATUS_LABELS ? (raw as OrderStatus) : "";
  });
  const [dateFrom, setDateFrom] = useState(() => getInitialParam("from"));
  const [dateTo, setDateTo] = useState(() => getInitialParam("to"));

  // ── Multi-tenant selection (ADR-021) ─────────────────────────────────────
  const {
    tenants: manageableTenants,
    selectedTenant,
    selectTenant,
  } = useTenantSelection();

  // ── Profile ──────────────────────────────────────────────────────────────

  useEffect(() => {
    profile()
      .then((p) => {
        setUserProfile(p);
        setProfileLoading(false);
      })
      .catch(() => setProfileLoading(false));
  }, [profile]);

  // ── Auth guard: preserve deep link across login ─────────────────────────

  useEffect(() => {
    if (!authed) {
      // Same pattern as other pages (e.g. CategoriesManagementPage): LoginPage
      // reads location.state.from and navigates back after successful login.
      navigate("/login", {
        state: { from: location.pathname + location.search },
        replace: true,
      });
    }
  }, [authed, navigate, location.pathname, location.search]);

  // ── URL sync ─────────────────────────────────────────────────────────────

  const buildQueryString = useCallback(
    (
      page: number,
      filters?: {
        search: string;
        statusFilter: string;
        dateFrom: string;
        dateTo: string;
      },
    ) => {
      const s = filters?.search ?? search;
      const sf = filters?.statusFilter ?? statusFilter;
      const from = filters?.dateFrom ?? dateFrom;
      const to = filters?.dateTo ?? dateTo;
      const params = new URLSearchParams();
      params.set("page", String(page));
      if (s) params.set("search", s);
      if (sf) params.set("status", sf);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return params.toString();
    },
    [search, statusFilter, dateFrom, dateTo],
  );

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchOrders = useCallback(
    async (
      page: number,
      filters?: {
        search: string;
        statusFilter: string;
        dateFrom: string;
        dateTo: string;
      },
    ) => {
      setOrdersLoading(true);
      try {
        const s = filters?.search ?? search;
        const sf = filters?.statusFilter ?? statusFilter;
        const from = filters?.dateFrom ?? dateFrom;
        const to = filters?.dateTo ?? dateTo;
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("per_page", String(perPage));
        if (s) params.set("search", s);
        if (sf) params.set("status", sf);
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        if (sortBy) params.set("sort_by", sortBy);
        if (sortOrder) params.set("sort_dir", sortOrder);

        const res = await api.get<OmsOrdersListResponse>(
          `${API_URLS.OMS_ADMIN_ORDERS}?${params}`,
        );
        setOrders(res.data.orders || []);
        setOrdersTotal(res.data.total || 0);
        setOrdersTotalPages(res.data.total_pages || 1);
        setOrdersPage(page);

        history.replaceState(
          history.state,
          "",
          `/oms-orders?${buildQueryString(page, {
            search: s,
            statusFilter: sf,
            dateFrom: from,
            dateTo: to,
          })}`,
        );
      } catch (e: any) {
        if ([401, 403].includes(e?.response?.status)) {
          setNeedsRelogin(true);
        } else {
          setError(e?.response?.data?.error || "Erro ao carregar encomendas.");
        }
      } finally {
        setOrdersLoading(false);
      }
    },
    [
      search,
      statusFilter,
      dateFrom,
      dateTo,
      sortBy,
      sortOrder,
      perPage,
      buildQueryString,
      setError,
    ],
  );

  // Initial load and re-fetch when sort/perPage change
  useEffect(() => {
    if (!profileLoading && userProfile) {
      fetchOrders(ordersPage);
    }
    // intentionally omit ordersPage to avoid double-fetch on page change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, userProfile, sortBy, sortOrder, perPage]);

  // ── Detail modal ─────────────────────────────────────────────────────────

  const openOrderDetail = (order: OmsOrder) => {
    const detailPath = `/oms-orders/${order.id}`;
    // When arriving via deep link the URL already is the detail path — don't
    // push a duplicate history entry (would make Back reopen the modal after
    // close). Only push when navigating from the list.
    if (window.location.pathname !== detailPath) {
      history.pushState(history.state, "", detailPath);
    }
    setModalScreen({
      title: `Encomenda ${orderShortRef(order.id)}`,
      state: "big-view",
      childrenControlOverflow: true,
      children: (
        <OmsOrderDetail
          order={order}
          onOrderUpdated={(updated) => {
            setOrders((prev) =>
              prev.map((o) => (o.id === updated.id ? updated : o)),
            );
          }}
        />
      ),
      onClose: () => {
        // replaceState (not pushState): the detail entry is replaced by the
        // list URL so browser Back after closing doesn't reopen the modal.
        history.replaceState(
          history.state,
          "",
          `/oms-orders?${buildQueryString(ordersPage)}`,
        );
        setModalScreen(null);
      },
    });
  };

  // ── Deep link: /oms-orders/:orderId opens the detail directly ────────────

  const deepLinkOpenedRef = useRef(false);

  useEffect(() => {
    if (!orderId || profileLoading || !userProfile) return;
    if (!userProfile.isAdmin() && !userProfile.isCommercial()) return;
    if (deepLinkOpenedRef.current) return;
    deepLinkOpenedRef.current = true;

    let cancelled = false;
    api
      .get<OmsOrder>(`${API_URLS.OMS_ADMIN_ORDERS}/${orderId}`)
      .then((res) => {
        if (!cancelled) openOrderDetail(res.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        // Unknown/invalid order — fall back to the list with an error banner.
        history.replaceState(
          history.state,
          "",
          `/oms-orders?${buildQueryString(1)}`,
        );
        setError(
          e?.response?.status === 404
            ? "Encomenda não encontrada."
            : e?.response?.data?.error || "Erro ao abrir a encomenda.",
        );
      });
    return () => {
      cancelled = true;
    };
    // openOrderDetail is recreated per render; the ref gate guarantees a
    // single run per orderId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, profileLoading, userProfile]);

  // ── Guards ────────────────────────────────────────────────────────────────

  if (!authed) return null;

  if (profileLoading)
    return (
      <div className="flex items-center justify-center p-12 text-gray-500">
        A carregar...
      </div>
    );

  if (!userProfile?.isAdmin() && !userProfile?.isCommercial())
    return <Navigate to="/" replace />;

  if (needsRelogin)
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            Sessão desatualizada
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            A sua sessão foi emitida antes das permissões de encomendas serem
            configuradas. Por favor, faça logout e volte a entrar para
            continuar.
          </p>
          <a href="/logout" className={PRIMARY_BUTTON_CLASS}>
            Fazer logout
          </a>
        </div>
      </div>
    );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
            Gestão de Encomendas
          </h1>
          <p className="text-gray-500 mt-1">
            Consulte e gira encomendas do canal online.
          </p>
        </div>

        {/* ── Filters ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TenantSelector
              tenants={manageableTenants}
              value={selectedTenant}
              onChange={(t) => {
                selectTenant(t);
                // The interceptor reads the selection from storage, so the
                // refetch is scoped to the newly selected tenant.
                fetchOrders(1);
              }}
            />
            <div>
              <label htmlFor="oms-search" className={LABEL_CLASS}>
                Pesquisar
              </label>
              <input
                id="oms-search"
                type="text"
                className={FIELD_CLASS}
                placeholder="Nome, email, ref…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchOrders(1)}
              />
            </div>
            <div>
              <label htmlFor="oms-status" className={LABEL_CLASS}>
                Estado
              </label>
              <select
                id="oms-status"
                className={FIELD_CLASS}
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as OrderStatus | "")
                }
              >
                <option value="">Todos os estados</option>
                {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map(
                  (s) => (
                    <option key={s} value={s}>
                      {ORDER_STATUS_LABELS[s]}
                    </option>
                  ),
                )}
              </select>
            </div>
            <div>
              <label htmlFor="oms-from" className={LABEL_CLASS}>
                De
              </label>
              <input
                id="oms-from"
                type="date"
                className={FIELD_CLASS}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="oms-to" className={LABEL_CLASS}>
                Até
              </label>
              <input
                id="oms-to"
                type="date"
                className={FIELD_CLASS}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                className={PRIMARY_BUTTON_CLASS}
                onClick={() => fetchOrders(1)}
              >
                Pesquisar
              </button>
              {(search || statusFilter || dateFrom || dateTo) && (
                <button
                  className={SECONDARY_BUTTON_CLASS}
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                    setDateFrom("");
                    setDateTo("");
                    // Fetch with explicit empty filters — the state closure would
                    // refetch with the pre-clear values.
                    fetchOrders(1, {
                      search: "",
                      statusFilter: "",
                      dateFrom: "",
                      dateTo: "",
                    });
                  }}
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── List ── */}
        <div className="overflow-auto">
          <ListOmsOrderItems
            orders={orders}
            page={ordersPage}
            perPage={perPage}
            setPerPage={(p) => {
              setPerPage(p);
            }}
            sortBy={sortBy}
            setSortBy={(f) => {
              setSortBy(f);
            }}
            sortOrder={sortOrder}
            setSortOrder={(o) => {
              setSortOrder(o);
            }}
            totalPages={ordersTotalPages}
            totalResults={ordersTotal}
            loading={ordersLoading}
            handlePageChange={(p) => fetchOrders(p)}
            openOrderDetail={openOrderDetail}
          />
        </div>
      </div>
    </div>
  );
};

export default OmsOrdersPage;
